import React from 'react';
import styled, { keyframes } from 'styled-components';

// ================= ANIMAÇÕES =================
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

// ================= ESTILOS =================
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Sem backdrop-filter: blur — força repaint contínuo da tela inteira e travava o modal
     durante a geração do relatório. O overlay escuro sozinho já dá o contraste necessário. */
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: ${fadeIn} 0.15s ease-out;
  /* Sobe o overlay para uma layer própria de composição — evita que mudanças atrás dele
     gerem invalidações da viewport principal. */
  will-change: opacity;
  transform: translateZ(0);
`;

const ModalContent = styled.div`
  background: #ffffff;
  padding: 40px 48px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: ${scaleIn} 0.3s ease-out;
  min-width: 240px;
`;

const SpinnerContainer = styled.div`
  position: relative;
  width: 52px;
  height: 52px;
  margin: 0 auto 20px;
`;

const SpinnerRing = styled.div`
  position: absolute;
  inset: 0;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 0.55s linear infinite;
`;

const SpinnerRingInner = styled.div`
  position: absolute;
  inset: 6px;
  border: 3px solid transparent;
  border-bottom-color: #93c5fd;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite reverse;
`;

const Message = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;

const SubMessage = styled.p`
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const Dot = styled.span<{ delay: string }>`
  animation: ${pulse} 0.9s ease-in-out infinite;
  animation-delay: ${props => props.delay};
`;

// ================= COMPONENTE =================
interface ProgressBarModalProps {
  carregando: boolean;
  titulo: string;
}

const ProgressBarModal: React.FC<ProgressBarModalProps> = ({ carregando, titulo }) => {
  if (!carregando) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <SpinnerContainer>
          <SpinnerRing />
          <SpinnerRingInner />
        </SpinnerContainer>
        <Message>{titulo}</Message>
        <SubMessage>
          Aguarde
          <Dot delay="0s">.</Dot>
          <Dot delay="0.2s">.</Dot>
          <Dot delay="0.4s">.</Dot>
        </SubMessage>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProgressBarModal;
